import { HelpCircle, InfoCircle } from '@untitledui/icons'
import { type ComponentType, type HTMLAttributes, type ReactNode, type Ref, createContext, useContext } from 'react'
import { Group as AriaGroup, Input as AriaInput, TextField as AriaTextField } from 'react-aria-components'

import Tooltip from '@components/designSystem/Tooltip'
import { HintText } from '@components/designSystem/input/hint-text'
import { Label } from '@components/designSystem/input/label'
import { cx, sortCx } from '@utils/cx.ts'

import type { InputProps as AriaInputProps, TextFieldProps as AriaTextFieldProps } from 'react-aria-components'

export interface InputIcon {
    icon: ComponentType<HTMLAttributes<HTMLOrSVGElement>>
    className?: string
    onClick?: () => void
    label?: string
}

interface InputIconProps extends InputIcon{
    isDisabled?: boolean
}

function InputIcon({ icon: Icon, className, isDisabled, onClick, label }: InputIconProps) {
  return (
    <Icon
      className={cx(
        'absolute size-5 text-fg-tertiary',
        onClick && !isDisabled ? 'cursor-pointer hover:text-fg-primary' : 'pointer-events-none',
        isDisabled && 'text-fg-disabled',
        className
      )}
      onClick={onClick}
      aria-label={label}
      role={onClick ? 'button' : undefined}
    />
  )
}

export interface InputBaseProps extends TextFieldProps {
    /** Tooltip message on hover. */
    tooltip?: string
    size?: 'sm' | 'md' | 'lg'
    /** Placeholder text. */
    placeholder?: string
    /** Class name for the input. */
    inputClassName?: string
    /** Class name for the input wrapper. */
    wrapperClassName?: string
    /** Class name for the tooltip. */
    tooltipClassName?: string
    /** Keyboard shortcut to display. */
    shortcut?: string | boolean
    ref?: Ref<HTMLInputElement>
    groupRef?: Ref<HTMLDivElement>
    /** Show a leading icon inside the input field. */
    leadingIcon?: InputIcon
    /** Show up to two trailing icons inside the input field. */
    trailingIcons?: [InputIcon] | [InputIcon, InputIcon]
}

function InputBase({
  ref,
  tooltip,
  shortcut,
  groupRef,
  size = 'sm',
  isInvalid,
  isDisabled,
  placeholder,
  wrapperClassName,
  tooltipClassName,
  inputClassName,
  // Omit this prop to avoid invalid HTML attribute warning
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isRequired: _isRequired,
  leadingIcon: leadingIconProps,
  trailingIcons,
  ...inputProps
}: Omit<InputBaseProps, 'label' | 'hint'>) {
  // Check if the input has a leading icon or tooltip
  const hasTrailingIcon = Boolean(tooltip ?? isInvalid ?? trailingIcons)
  const hasLeadingIcon = Boolean(leadingIconProps)
  const hasTwoTrailingIcons = trailingIcons?.length === 2
  const LeadingIcon = leadingIconProps?.icon
  const FirstTrailingIcon = trailingIcons?.[0]?.icon
  const SecondTrailingIcon = trailingIcons?.[1]?.icon

  // If the input is inside a `TextFieldContext`, use its context to simplify applying styles
  const context = useContext(TextFieldContext)

  const inputSize = context.size ?? size

  const sizes = sortCx({
    sm: {
      root: cx('px-4 py-2', hasLeadingIcon && 'pl-10', hasTrailingIcon && 'pr-9', hasTwoTrailingIcons && 'pr-18'),
      iconLeading: 'left-3',
      iconTrailing: hasTwoTrailingIcons ? 'right-11' : 'right-4',
      iconTrailingLast: 'right-4',
      shortcut: 'pr-2.5',
    },
    md: {
      root: cx('px-4.5 py-2.5', hasLeadingIcon && 'pl-10.5', hasTrailingIcon && 'pr-9.5', trailingIcons?.length === 2 && `
        pr-18.5
      `),
      iconLeading: 'left-3.5',
      iconTrailing: hasTwoTrailingIcons ? 'right-11' : 'right-5.5',
      iconTrailingLast: 'right-4',
      shortcut: 'pr-3',
    },
    lg: {
      root: cx('px-4.5 py-3.5', hasLeadingIcon && 'pl-11', hasTrailingIcon && 'pr-10', trailingIcons?.length === 2 && `
        pr-19
      `),
      iconLeading: 'left-3.5',
      iconTrailing: hasTwoTrailingIcons ? 'right-11' : 'right-3.5',
      iconTrailingLast: 'right-3.5',
      shortcut: 'pr-3.5',
    },
  })

  return (
    <AriaGroup
      {...{ isDisabled, isInvalid }}
      ref={groupRef}
      className={({ isFocusWithin, isDisabled, isInvalid }) =>
        cx(
          `
            relative flex w-full flex-row place-content-center place-items-center rounded-full shadow-xs ring-1
            ring-primary transition-shadow duration-100 ease-linear ring-inset
          `,
          'bg-gray-100 focus-within:bg-primary hover:bg-gray-200',

          isFocusWithin && !isDisabled && 'ring-1 ring-black',

          // Disabled state styles
          isDisabled && 'cursor-not-allowed bg-disabled ring-disabled',
          'group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled',

          // Invalid state styles
          isInvalid && 'ring-error_subtle',
          'group-invalid:ring-error_subtle',

          // Invalid state with focus-within styles
          isInvalid && isFocusWithin && 'ring-1 ring-error',
          isFocusWithin && 'group-invalid:ring-1 group-invalid:ring-error',

          context.wrapperClassName,
          wrapperClassName
        )
      }
    >
      {/* Leading icon */}
      {LeadingIcon && (
        <InputIcon
          {...leadingIconProps}
          className={cx(sizes[inputSize].iconLeading, leadingIconProps.className)}
          isDisabled={isDisabled}
        />
      )}

      {/* Input field */}
      <AriaInput
        {...(inputProps as AriaInputProps)}
        ref={ref}
        placeholder={placeholder}
        className={cx(
          `
            m-0 w-full bg-transparent text-md font-medium text-primary ring-0 outline-hidden
            placeholder:text-placeholder
            autofill:rounded-lg autofill:text-primary
            sm:text-sm
          `,
          isDisabled && 'cursor-not-allowed text-disabled',
          sizes[inputSize].root,
          context.inputClassName,
          inputClassName
        )}
      />

      {/* Tooltip and help icon */}
      {tooltip && !isInvalid && (
        <Tooltip title={tooltip}>
          <div
            className={cx(
              `
                absolute cursor-help text-fg-quaternary transition duration-200
                hover:text-fg-quaternary_hover
                focus:text-fg-quaternary_hover
              `,
              sizes[inputSize].iconTrailing,
              context.tooltipClassName,
              tooltipClassName
            )}
          >
            <HelpCircle className="size-4" />
          </div>
        </Tooltip>
      )}

      {/* Invalid icon */}
      {isInvalid && (
        <InfoCircle
          className={cx(
            'pointer-events-none absolute size-4 text-fg-error-secondary',
            sizes[inputSize].iconTrailing,
            context.tooltipClassName,
            tooltipClassName
          )}
        />
      )}

      {/* First Trailing icon */}
      {FirstTrailingIcon && (
        <InputIcon
          isDisabled={isDisabled}
          {...trailingIcons[0]}
          className={cx(sizes[inputSize].iconTrailing, trailingIcons[0].className)}
        />
      )}

      {/* Second Trailing icon */}
      {trailingIcons?.length === 2 && SecondTrailingIcon && (
        <InputIcon
          isDisabled={isDisabled}
          {...trailingIcons[1]}
          className={cx(sizes[inputSize].iconTrailingLast, trailingIcons[1].className)}
        />
      )}

      {/* Shortcut */}
      {shortcut && (
        <div
          className={cx(
            `
              pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit]
              bg-linear-to-r from-transparent to-bg-primary to-40% pl-8
            `,
            sizes[inputSize].shortcut
          )}
        >
          <span
            className={cx(
              `
                pointer-events-none rounded-sm px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary
                select-none ring-inset
              `,
              isDisabled && 'bg-transparent text-disabled'
            )}
            aria-hidden="true"
          >
            {typeof shortcut === 'string' ? shortcut : '⌘K'}
          </span>
        </div>
      )}
    </AriaGroup>
  )
}

interface BaseProps {
    /** Label text for the input */
    label?: string
    /** Helper text displayed below the input */
    hint?: ReactNode
}

interface TextFieldProps
    extends BaseProps,
        AriaTextFieldProps,
        Pick<InputBaseProps, 'size' | 'wrapperClassName' | 'inputClassName' | 'tooltipClassName'> {
    ref?: Ref<HTMLDivElement>
}

const TextFieldContext = createContext<TextFieldProps>({})

/**
 * TextField component serves as a wrapper that provides React Aria Components TextField functionality
 * with context support. It acts as a provider for input-related props and state, allowing child
 * components like InputBase to access shared configuration such as size, styling classes, and
 * validation state through the TextFieldContext.
 */
export function TextField({ className, ...props }: TextFieldProps) {
  return (
    <TextFieldContext.Provider value={props}>
      <AriaTextField
        {...props}
        data-input-wrapper
        className={(state) =>
          cx('group flex h-max w-full flex-col items-start justify-start gap-1.5', typeof className === 'function' ? className(state) : className)
        }
      />
    </TextFieldContext.Provider>
  )
}

interface InputProps extends InputBaseProps, BaseProps {
    /** Whether to hide required indicator from label */
    hideRequiredIndicator?: boolean
}

export function Input({
  size = 'sm',
  placeholder,
  label,
  hint,
  shortcut,
  hideRequiredIndicator,
  className,
  ref,
  groupRef,
  tooltip,
  inputClassName,
  wrapperClassName,
  tooltipClassName,
  leadingIcon,
  trailingIcons,
  ...props
}: InputProps) {
  return (
    <TextField aria-label={!label ? placeholder : undefined} {...props} className={className}>
      {({ isRequired, isInvalid, isDisabled }) => (
        <>
          {label && <Label isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired}>{label}</Label>}

          <InputBase
            {...{
              ref,
              groupRef,
              size,
              placeholder,
              shortcut,
              inputClassName,
              wrapperClassName,
              tooltipClassName,
              tooltip,
              isDisabled,
              leadingIcon,
              trailingIcons,
            }}
          />

          {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
        </>
      )}
    </TextField>
  )
}
